import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import ClustersDnDColumn, { type Item } from "@/components/Board";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, Search, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { visabilityAPI, type FoundationSummary } from "@/services/api";
import type { Brand } from "@/components/AppSidebar";
import { useVisibilityActionablesRealtime } from "@/hooks/useVisibilityActionablesRealtime";
import { Skeleton } from "@/components/ui/skeleton";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
} from "@/lib/posthog";
import { useAuth } from "@/components/AuthContext";

const toBoardStatus = (value?: string | null) => {
  switch (value) {
    case "inProgress":
      return "In Progress";
    case "resolved":
      return "Completed";
    case "canceled":
      return "Dismissed";
    default:
      return "To do";
  }
};

const toEffort = (value?: string | null) => {
  if (!value) return "medium";
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith("low")) return "low";
  if (normalized.startsWith("high")) return "high";
  return "medium";
};

const toPriority = (value?: string | null) => {
  if (!value) return "medium";
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith("low")) return "low";
  if (normalized.startsWith("high")) return "high";
  return "medium";
};

const formatCheckLabel = (value: string) => {
  switch (value) {
    case "ROBOTS_TXT":
      return "Robots.txt";
    case "SITEMAP":
      return "Sitemap";
    case "CRAWLABILITY_INDEXABILITY":
      return "Crawlability";
    case "STRUCTURED_DATA":
      return "Geo Audit";
    default:
      return value;
  }
};

export const Foundation = () => {
  const { locale, t } = useLocale();
  const { user } = useAuth();
  const [search, setSearch] = React.useState("");
  const [dndCards, setDndCards] = useState<Item[]>([]);
  const { clusterFilters, currentBrand, selectedProviders } = useOutletContext<{
    clusterFilters: any;
    currentBrand: Brand;
    selectedProviders?: string[];
  }>();
  const defaultDays = 30;
  const providersKey = selectedProviders?.join(",") || "all";
  const foundationQueryKey = [
    "get-cited-foundation",
    currentBrand?.brandId,
    defaultDays,
    providersKey,
    locale,
  ];
  const queryClient = useQueryClient();
  const trackedGetCitedViewRef = useRef<string>("");
  const trackedDashboardViewRef = useRef<string>("");

  const mapStatusToApi = (value: string) => {
    switch (value) {
      case "To do":
        return "toAddress";
      case "In Progress":
        return "inProgress";
      case "Completed":
        return "resolved";
      case "Dismissed":
        return "canceled";
      default:
        return null;
    }
  };

  const mapPriorityToApi = (value: string) => {
    switch (value) {
      case "low":
        return "Low";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      default:
        return null;
    }
  };

  const toStatusValue = (value?: string | null) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (normalized === "to do" || normalized === "toaddress") return "todo";
    if (normalized === "in progress" || normalized === "inprogress") {
      return "in_progress";
    }
    if (normalized === "completed" || normalized === "resolved") return "done";
    return "dismissed";
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: foundationQueryKey,
    queryFn: async () => {
      const res = await visabilityAPI.getActionables(
        currentBrand,
        defaultDays,
        undefined,
        undefined,
        { scope: "foundation" },
      );
      return res.data;
    },
    enabled: !!currentBrand?.brandId,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useVisibilityActionablesRealtime({
    brandId: currentBrand?.brandId,
    days: defaultDays,
    providers: selectedProviders,
    scope: "foundation",
    queryKey: foundationQueryKey,
    enabled: !!currentBrand?.brandId,
  });

  const foundation = useMemo<FoundationSummary | null>(() => {
    return (data?.foundation as FoundationSummary) || null;
  }, [data]);
  const showLoading = isLoading || isFetching;

  const cards = useMemo(() => {
    const order = [
      "ROBOTS_TXT",
      "SITEMAP",
      "CRAWLABILITY_INDEXABILITY",
      "STRUCTURED_DATA",
    ];
    const orderMap = new Map(order.map((key, index) => [key, index]));
    return (foundation?.checks ?? [])
      .filter(
        (check) => check.key !== "ENTITY_CLARITY" && check.key !== "LLMS_TXT",
      )
      .slice()
      .sort((a, b) => {
        const aIndex = orderMap.get(a.key) ?? Number.MAX_SAFE_INTEGER;
        const bIndex = orderMap.get(b.key) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
  }, [foundation]);

  const [nowMs, setNowMs] = useState(() => Date.now());
  const FOUNDATION_INTERVAL_DAYS = 7;

  const foundationItems = useMemo<Item[]>(() => {
    if (!foundation?.tasks?.length) return [];
    return foundation.tasks
      .filter((task) => task.checkKey !== "ENTITY_CLARITY")
      .map((task) => {
        const subtasks = Array.isArray(task.subtasks) ? task.subtasks : [];
        const originalSubtasks = Array.isArray(task.original?.subtasks)
          ? task.original.subtasks
          : null;
        const completedItems = subtasks.filter(
          (subtask: any) => subtask?.done,
        ).length;
        return {
          id: task.id,
          checkKey: task.checkKey || null,
          status: toBoardStatus(task.status),
          title: task.title || task.checkKey || "Foundation task",
          description: task.description || "",
          tag: task.taskType || "Technical",
          completedITems: completedItems,
          allItems: subtasks.length || 0,
          priority: toPriority(task.priority),
          effort: toEffort(task.taskType),
          prompts: [],
          clusterId: task.clusterId || "",
          rationale: task.rationale || "",
          impact: task.impact || "",
          original: task.original
            ? {
                title: task.original.title || "",
                description: task.original.description || "",
                rationale: task.original.rationale || "",
                impact: task.original.impact || "",
                tasks: originalSubtasks
                  ? originalSubtasks.map((subtask: any) => ({
                      title: subtask?.title || "Subtask",
                      description:
                        subtask?.details || subtask?.description || "",
                      done: Boolean(subtask?.done),
                    }))
                  : null,
              }
            : null,
          evidence: task.evidence || {},
          isFoundation: true,
          sortIndex: task.sortIndex ?? null,
          actionType: "foundation",
          sourceType: "aggregator",
          difficulty: toEffort(task.taskType),
          tasks: subtasks.map((subtask: any) => ({
            title: subtask?.title || "Subtask",
            description: subtask?.details || subtask?.description || "",
            done: Boolean(subtask?.done),
          })),
        };
      });
  }, [foundation]);

  const sortedFoundationItems = useMemo<Item[]>(() => {
    const statusOrder: Record<string, number> = {
      "To do": 0,
      "In Progress": 1,
      Completed: 2,
      Dismissed: 3,
    };
    const taskMeta = new Map(
      (foundation?.tasks ?? []).map((task) => [
        task.id,
        {
          updatedAt: task.updatedAt ? new Date(task.updatedAt).getTime() : 0,
          sortIndex: typeof task.sortIndex === "number" ? task.sortIndex : null,
        },
      ]),
    );
    return foundationItems.slice().sort((a, b) => {
      const statusDelta =
        (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDelta) return statusDelta;
      const aMeta = taskMeta.get(a.id);
      const bMeta = taskMeta.get(b.id);
      const aIndex = aMeta?.sortIndex;
      const bIndex = bMeta?.sortIndex;
      if (
        aIndex !== null &&
        aIndex !== undefined &&
        bIndex !== null &&
        bIndex !== undefined
      ) {
        if (aIndex !== bIndex) return aIndex - bIndex;
      } else if (aIndex !== null && aIndex !== undefined) {
        return -1;
      } else if (bIndex !== null && bIndex !== undefined) {
        return 1;
      }
      const aUpdated = aMeta?.updatedAt ?? 0;
      const bUpdated = bMeta?.updatedAt ?? 0;
      return bUpdated - aUpdated;
    });
  }, [foundationItems, foundation?.tasks]);

  useEffect(() => {
    const filtered = sortedFoundationItems.filter((item) => {
      let effortResult = true;
      let priorityResult = true;
      let typeResult = true;

      if (clusterFilters?.effort) {
        effortResult = item.effort === clusterFilters.effort;
      }

      if (clusterFilters?.priority) {
        priorityResult = item.priority === clusterFilters.priority;
      }

      if (clusterFilters?.type) {
        typeResult = item.tag === clusterFilters.type;
      }

      return effortResult && priorityResult && typeResult;
    });
    setDndCards(filtered);
  }, [clusterFilters, sortedFoundationItems]);

  useEffect(() => {
    const searchedItems = sortedFoundationItems.filter(
      (card: { title: string; description: string }) =>
        (card.title || card.description)
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
    );
    setDndCards(searchedItems);
  }, [search, sortedFoundationItems]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60000);
    return () => window.clearInterval(interval);
  }, []);

  const nextRunLabel = useMemo(() => {
    if (!foundation?.lastCheckedAt) {
      return "Next run: pending";
    }
    const lastCheckedMs = new Date(foundation.lastCheckedAt).getTime();
    if (Number.isNaN(lastCheckedMs)) {
      return "Next run: pending";
    }
    const nextRunMs =
      lastCheckedMs + FOUNDATION_INTERVAL_DAYS * 24 * 60 * 60 * 1000;
    const remainingMs = nextRunMs - nowMs;
    if (remainingMs <= 0) {
      return "Next run: due";
    }
    const totalMinutes = Math.ceil(remainingMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (!days && !hours) parts.push(`${minutes}m`);
    return `Next run: ${parts.join(" ")}`;
  }, [foundation?.lastCheckedAt, nowMs]);

  const handleStatusChange = async (
    id: string,
    status: string,
    _?: number | null,
    statusOrderIds?: string[],
    sourceOrderIds?: string[],
    sourceStatus?: string,
  ) => {
    if (!currentBrand?.brandId) return;
    const nextStatus = mapStatusToApi(status);
    if (!nextStatus) return;
    try {
      const foundationTaskMap = new Map(
        (foundation?.tasks || []).map((task) => [task.id, task]),
      );
      const previousTask = foundationTaskMap.get(id);
      capturePosthogEvent("action_status_changed", {
        brand_id: currentBrand.brandId,
        cluster_id: null,
        action_id: id,
        action_type: "foundation_audit",
        from_status: sourceStatus
          ? toStatusValue(sourceStatus)
          : toStatusValue(previousTask?.status),
        to_status: toStatusValue(status),
        changed_via: sourceStatus ? "drag_drop" : "menu",
        source: "foundation",
      });

      queryClient.setQueryData(foundationQueryKey, (prev: any) => {
        if (!prev?.foundation?.tasks) return prev;
        return {
          ...prev,
          foundation: {
            ...prev.foundation,
            tasks: prev.foundation.tasks.map((task: any) => {
              const next: any = { ...task };
              if (task.id === id) {
                next.status = nextStatus;
              }
              if (statusOrderIds?.length) {
                const nextIndex = statusOrderIds.indexOf(task.id);
                if (nextIndex !== -1) {
                  next.sortIndex = nextIndex;
                }
              }
              if (sourceOrderIds?.length) {
                const nextIndex = sourceOrderIds.indexOf(task.id);
                if (nextIndex !== -1) {
                  next.sortIndex = nextIndex;
                }
              }
              return next;
            }),
          },
        };
      });

      const columns: Record<
        "toAddress" | "inProgress" | "resolved" | "canceled",
        { id: string; sortIndex?: number }[]
      > = {
        toAddress: [],
        inProgress: [],
        resolved: [],
        canceled: [],
      };
      if (statusOrderIds?.length) {
        columns[nextStatus].push(
          ...statusOrderIds.map((entryId, index) => ({
            id: entryId,
            sortIndex: index,
          })),
        );
      }
      if (sourceOrderIds?.length && sourceStatus) {
        const sourceStatusApi = mapStatusToApi(sourceStatus);
        if (sourceStatusApi) {
          columns[sourceStatusApi].push(
            ...sourceOrderIds.map((entryId, index) => ({
              id: entryId,
              sortIndex: index,
            })),
          );
        }
      }
      if (!columns[nextStatus].length) {
        columns[nextStatus].push({ id, sortIndex: 0 });
      }

      await visabilityAPI.reorderActionables({
        brandId: currentBrand.brandId,
        columns,
      });
    } catch {
      // ignore update errors for now
    }
  };

  const handlePriorityChange = async (id: string, priority: string) => {
    if (!currentBrand?.brandId) return;
    const nextPriority = mapPriorityToApi(priority);
    if (!nextPriority) return;
    try {
      await visabilityAPI.updateActionableById({
        id,
        brandId: currentBrand.brandId,
        priority: nextPriority,
      });
    } catch {
      // ignore update errors for now
    }
  };

  const handleSubtasksChange = async (id: string, tasks: any[]) => {
    if (!currentBrand?.brandId) return;
    const normalizedTasks = tasks.map((task) => ({
      ...task,
      details: task.details ?? task.description ?? "",
    }));
    try {
      queryClient.setQueryData(foundationQueryKey, (prev: any) => {
        if (!prev?.foundation?.tasks) return prev;
        return {
          ...prev,
          foundation: {
            ...prev.foundation,
            tasks: prev.foundation.tasks.map((task: any) =>
              task.id === id ? { ...task, subtasks: normalizedTasks } : task,
            ),
          },
        };
      });
      await visabilityAPI.updateActionableById({
        id,
        brandId: currentBrand.brandId,
        subtasks: normalizedTasks,
      });
    } catch {
      // ignore update errors for now
    }
  };

  useEffect(() => {
    if (!currentBrand?.brandId || showLoading) return;
    const actionsCount = foundationItems.length;
    const statusCounts = {
      todo: 0,
      in_progress: 0,
      done: 0,
      dismissed: 0,
    };
    foundationItems.forEach((item) => {
      const status = toStatusValue(item.status);
      if (status in statusCounts) {
        (statusCounts as any)[status] += 1;
      }
    });
    const key = `${currentBrand.brandId}:${providersKey}:${actionsCount}`;
    if (trackedGetCitedViewRef.current === key) return;
    trackedGetCitedViewRef.current = key;

    capturePosthogEvent("foundation_viewed", {
      brand_id: currentBrand.brandId,
      has_tasks: actionsCount > 0,
      tasks_count: actionsCount,
      tasks_by_status: statusCounts,
    });
  }, [
    currentBrand?.brandId,
    foundationItems.length,
    providersKey,
    showLoading,
  ]);

  useEffect(() => {
    if (!currentBrand?.brandId || showLoading) return;
    const hasData = foundationItems.length > 0;
    const key = `${currentBrand.brandId}:${hasData}`;
    if (trackedDashboardViewRef.current === key) return;
    trackedDashboardViewRef.current = key;
    capturePosthogEvent("dashboard_viewed", {
      brand_id: currentBrand.brandId,
      dashboard: "foundation",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId: currentBrand.brandId,
        dashboard: "foundation",
      }),
      has_data: hasData,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(currentBrand.brandId) ?? undefined,
    });
  }, [currentBrand?.brandId, foundationItems.length, showLoading, user?.id]);

  const handleActionOpened = (item: Item) => {
    if (!currentBrand?.brandId) return;
    capturePosthogEvent("action_opened", {
      brand_id: currentBrand.brandId,
      cluster_id: null,
      action_id: item.id,
      action_type: "foundation_audit",
      source: "foundation",
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-x-hidden">
      <div className="flex gap-2 flex-col lg:flex-row items-start">
        <div className=" text-[#737373] text-sm grow">
          <LocalizedText id="legacy.your.baseline.to.get.cited.we.check.crawlability.12v5dzc" />
          <Popover>
            <PopoverTrigger asChild>
              <span className="cursor-pointer ml-1 text-[#FF5E00] font-semibold">
                <LocalizedText id="legacy.learn.more.1o48nb8" />
              </span>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="flex gap-4">
                <div className="space-y-2">
                  <h4 className="leading-none text-base font-medium mb-4">
                    <LocalizedText id="legacy.how.it.works.1pizet" />
                  </h4>
                  <ul className="text-muted-foreground text-sm list-disc pl-6 space-y-2">
                    <li>
                      <LocalizedText id="legacy.weekly.refresh.of.your.website.checks.robots.crawlability.po2uzn" />
                    </li>
                    <li>
                      <LocalizedText id="legacy.if.a.check.fails.we.automatically.create.a.1021jsl" />
                    </li>
                    <li>
                      <LocalizedText id="legacy.update.status.and.priority.to.track.progress.well.14mcfaa" />
                    </li>
                  </ul>
                </div>
                <div className="grid gap-2"></div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center justify-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder={t("legacy.search.14mmb9h")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 "
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="px-0 cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="text-sm text-muted-foreground whitespace-nowrap">
        {nextRunLabel}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {showLoading && !foundation && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Spinner className="text-[#FF5E00]" size={16} />
            <LocalizedText id="legacy.loading.foundation.checks.1cxf4j" />
          </div>
        )}
        {cards.map((card) => {
          const statusColor =
            card.status === "Bad"
              ? "border-red-300 bg-red-50 text-red-600"
              : "border-green-400 bg-green-50 text-green-600";
          return (
            <div
              key={card.key}
              className={`border rounded-xl p-2 ${statusColor}`}
            >
              {showLoading ? (
                <div className="flex items-center space-x-6">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full bg-black/5" />
                  <div className="space-y-[14px] w-full">
                    <Skeleton className="h-4 w-full rounded-full bg-black/5" />
                    <Skeleton className="h-4 w-[52%] rounded-full bg-black/5" />
                    <Skeleton className="h-4 w-[52%] rounded-full bg-black/5" />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {formatCheckLabel(card.key)}
                    </h3>
                    <div className="flex gap-3 items-center font-medium mt-1">
                      <div className="rounded-full border border-1 border-current p-1 h-fit">
                        <Check strokeWidth={4} size={10}></Check>
                      </div>
                      {card.status}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {card.status === "Bad"
                        ? "Requires attention"
                        : "Looks good"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!isLoading && !isFetching && !cards.length && (
          <div className="text-sm text-muted-foreground">
            <LocalizedText id="legacy.no.foundation.checks.yet.182pz7g" />
          </div>
        )}
      </div>

      <div className="relative min-h-0 flex-1 max-w-full overflow-auto overscroll-contain border-t border-[#E5E5E5] pt-3">
        <ClustersDnDColumn
          loading={showLoading}
          showBoostPrompts={false}
          INITIAL_ITEMS={dndCards || []}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onSubtasksChange={handleSubtasksChange}
          onItemOpened={handleActionOpened}
        ></ClustersDnDColumn>
      </div>
    </div>
  );
};
